import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard, BookOpen, Upload, Settings, LogOut, RefreshCw,
  CheckCircle, Clock, AlertTriangle, XCircle, ChevronRight,
  HardDrive, Cpu, Zap, TrendingUp, FileText, Trash2, Edit3,
  Eye, Download, Search, Filter, MoreVertical, X, Save,
  Cloud, Database, Activity, BarChart3, PieChart, Calendar,
  Lock, Key, Mail, Bell, User, Camera, Users, UserPlus, ExternalLink, BookText, Brain
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

async function readJsonBody(res) {
  try {
    // Use a clone so body-read issues in one parser path
    // do not consume the original response stream.
    const clone = res.clone();
    const text = await clone.text();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  } catch {
    // Some browsers/extensions can consume the stream before app code reads it.
    // Return null so caller can still show a clean fallback message.
    return null;
  }
}

function formatApiErrorMessage(data, fallback) {
  if (!data) return fallback;
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object' && typeof d.message === 'string') return d.message;
  if (d && typeof d === 'object' && Array.isArray(d)) {
    return d.map((x) => (typeof x === 'string' ? x : JSON.stringify(x))).join(' ');
  }
  return fallback;
}

/** En dash (matches required copy) — use explicit code unit so the page line always renders clearly. */
const EN_DASH = '\u2013';

/** GDrive Import & Process: prefer pdf_name + page + issue_kind so the page number is never dropped. */
function formatGdriveImportError(res, data, pdfFileName) {
  const nameFromRow = pdfFileName && String(pdfFileName).trim() ? pdfFileName : null;

  if (data && typeof data === 'object') {
    let d = data.detail;
    if (typeof d === 'string') {
      try {
        const parsed = JSON.parse(d);
        if (parsed && typeof parsed === 'object') d = parsed;
      } catch {
        if (d.trim()) return d.trim();
      }
    }

    if (typeof d === 'string' && d.trim()) return d.trim();

    if (d && typeof d === 'object' && !Array.isArray(d)) {
      const msg = d.message;
      if (typeof msg === 'string' && msg.trim()) return msg.trim();

      if (Array.isArray(d.issues) && d.issues.length > 0) {
        const pdfName = String(d.pdf_name || nameFromRow || 'PDF').trim();
        const lines = [`Import Failed: "${pdfName}" contains issues.`];
        d.issues.forEach((it) => {
          const page = Number(it?.page);
          const kind = String(it?.kind || '').toLowerCase();
          const label = kind === 'missing' ? 'Missing image' : 'Blurred image';
          if (Number.isFinite(page) && page >= 1) lines.push(`- Page ${page}: ${label}`);
        });
        if (lines.length > 1) return lines.join('\n');
      }

      const pdfName = String(d.pdf_name || nameFromRow || 'PDF').trim();
      const pageRaw = d.page;
      const pageNum =
        pageRaw !== undefined && pageRaw !== null && pageRaw !== ''
          ? Number(pageRaw)
          : NaN;
      const kind = String(d.issue_kind || '').toLowerCase();

      if (
        Number.isFinite(pageNum) &&
        pageNum >= 1 &&
        (kind === 'blurred' || kind === 'missing')
      ) {
        if (kind === 'missing') {
          return `${pdfName} ${EN_DASH} Page ${pageNum} has a missing image. Not able to import.`;
        }
        return `${pdfName} ${EN_DASH} Page ${pageNum} has a blurred image. Not able to import.`;
      }
    }

    if (Array.isArray(d) && d[0] && typeof d[0].msg === 'string') {
      return d[0].msg;
    }

    if (typeof data.message === 'string' && data.message.trim()) return data.message.trim();
  }

  const label = nameFromRow || 'PDF';
  return `${label} ${EN_DASH} Import failed (HTTP ${res.status}).`;
}

const AdminDashboard = ({ token, admin, onLogout }) => {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [stats, setStats] = useState(null);
  const [books, setBooks] = useState([]);
  const [gdriveBooks, setGdriveBooks] = useState([]);
  const [gdriveConnected, setGdriveConnected] = useState(false);
  const [gdriveFolderUrl, setGdriveFolderUrl] = useState('');
  const [gdriveLoading, setGdriveLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [booksLoadError, setBooksLoadError] = useState('');
  const [selectedBook, setSelectedBook] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({ title: '', emoji: '', level: '' });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(admin?.picture || null);
  
  // Bulk User Creation state
  const [showUserModal, setShowUserModal] = useState(false);
  const [userFormData, setUserFormData] = useState({ series_name: '', num_students: 10 });
  const [userCreating, setUserCreating] = useState(false);
  const [userCreateResult, setUserCreateResult] = useState(null);
  const [userSeries, setUserSeries] = useState([]);

  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingGdriveBook, setPendingGdriveBook] = useState(null);
  const [importDescription, setImportDescription] = useState('');
  const [importProcessing, setImportProcessing] = useState(false);
  const [importDescriptionError, setImportDescriptionError] = useState('');

  // Avatar options
  const avatarOptions = [
    '👤', '👨‍💼', '👩‍💼', '🧑‍💻', '👨‍🔧', '👩‍🔧', '🦸', '🦹', '🧙', '🧑‍🚀',
    '🐱', '🐶', '🦊', '🐼', '🐨', '🦁', '🐯', '🐸', '🦉', '🐝'
  ];

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, booksRes, gdriveRes] = await Promise.all([
        fetch(`${API}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/books/uploaded`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/admin/gdrive/books`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (booksRes.ok) {
        const booksData = await booksRes.json();
        setBooks(Array.isArray(booksData) ? booksData : []);
        setBooksLoadError('');
      } else {
        const errBody = await booksRes.json().catch(() => ({}));
        setBooksLoadError(errBody.detail || `Failed to load books (HTTP ${booksRes.status})`);
        setBooks([]);
      }
      if (gdriveRes.ok) {
        const gdriveData = await gdriveRes.json();
        if (gdriveData.connected !== undefined) {
          setGdriveConnected(gdriveData.connected);
          setGdriveBooks(gdriveData.files || []);
          setGdriveFolderUrl(gdriveData.folder_url || '');
        } else {
          // Legacy format (array of books)
          setGdriveConnected(true);
          setGdriveBooks(gdriveData);
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setBooksLoadError(err?.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fetch user series on mount
  useEffect(() => {
    if (token) {
      fetchUserSeries();
    }
  }, [token]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleConnectGoogleDrive = async () => {
    setGdriveLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/gdrive/connect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        const errBody = await res.json().catch(() => ({}));
        const message = errBody.detail || `Failed to initiate Google Drive connection (HTTP ${res.status})`;
        console.error(message);
        window.alert(message);
      }
    } catch (err) {
      console.error('Failed to connect Google Drive:', err);
    } finally {
      setGdriveLoading(false);
    }
  };

  const handleDisconnectGoogleDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/gdrive/disconnect`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setGdriveConnected(false);
        setGdriveBooks([]);
      }
    } catch (err) {
      console.error('Failed to disconnect Google Drive:', err);
    }
  };

  const handleDeleteBook = async (bookId) => {
    if (!confirm('Are you sure you want to delete this book?')) return;
    
    try {
      const res = await fetch(`${API}/api/admin/books/${bookId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setBooks(books.filter(b => b.id !== bookId));
      }
    } catch (err) {
      console.error('Failed to delete book:', err);
    }
  };

  const handleEditBook = (book) => {
    setSelectedBook(book);
    setEditFormData({
      title: book.title || '',
      emoji: book.emoji || '📚',
      level: book.level || 'K-3'
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedBook) return;
    
    try {
      const res = await fetch(`${API}/api/admin/books/${selectedBook.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(editFormData)
      });
      
      if (res.ok) {
        // Update local state
        setBooks(books.map(b => 
          b.id === selectedBook.id 
            ? { ...b, ...editFormData }
            : b
        ));
        setShowEditModal(false);
        setSelectedBook(null);
        alert('Book updated successfully!');
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to update book');
      }
    } catch (err) {
      console.error('Failed to save book:', err);
      alert('Failed to save book. Please try again.');
    }
  };

  // Fetch user series data
  const fetchUserSeries = async () => {
    try {
      const res = await fetch(`${API}/api/admin/users/series`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserSeries(data);
      }
    } catch (err) {
      console.error('Failed to fetch user series:', err);
    }
  };
  

  // Bulk create users handler
  const handleBulkCreateUsers = async () => {
    if (!userFormData.series_name || userFormData.num_students < 1) {
      alert('Please enter a valid series name and number of students');
      return;
    }

    setUserCreating(true);
    setUserCreateResult(null);

    try {
      const res = await fetch(`${API}/api/admin/users/bulk-create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(userFormData)
      });

      const data = await res.json();

      if (res.ok) {
        setUserCreateResult(data);
        setUserFormData({ ...userFormData, series_name: '' });
        fetchUserSeries(); // Refresh series list
        
        if (data.sheet_url) {
          // Open the sheet in a new tab
          window.open(data.sheet_url, '_blank');
        }
      } else {
        const message = data.detail || data.message || 'Failed to create users';
        alert(typeof message === 'string' ? message : 'Failed to create users');
      }
    } catch (err) {
      console.error('Failed to create users:', err);
      alert('Failed to create users. Please try again.');
    } finally {
      setUserCreating(false);
    }
  };

  const handleApproveBook = async (bookId) => {
    try {
      const res = await fetch(`${API}/api/admin/books/${bookId}/approve`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Failed to approve book:', err);
    }
  };

  const handleRegenerateVocabulary = async (bookId, bookTitle) => {
    if (!confirm(`Regenerate vocabulary for "${bookTitle}"? This will replace existing vocabulary.`)) return;
    
    try {
      const res = await fetch(`${API}/api/admin/books/${bookId}/regenerate-vocabulary`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        alert(data.message || 'Vocabulary regenerated successfully!');
        fetchData();
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to regenerate vocabulary');
      }
    } catch (err) {
      console.error('Failed to regenerate vocabulary:', err);
      alert('Failed to regenerate vocabulary. Please try again.');
    }
  };

  const handleRegenerateQuiz = async (bookId, bookTitle) => {
    if (!confirm(`Regenerate quiz for "${bookTitle}"? This will replace existing quiz questions.`)) return;
    
    try {
      const res = await fetch(`${API}/api/admin/books/${bookId}/regenerate-quiz`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        alert(data.message || 'Quiz regenerated successfully!');
        fetchData();
      } else {
        const error = await res.json();
        alert(error.detail || 'Failed to regenerate quiz');
      }
    } catch (err) {
      console.error('Failed to regenerate quiz:', err);
      alert('Failed to regenerate quiz. Please try again.');
    }
  };

  const openImportModal = (book) => {
    setPendingGdriveBook(book);
    setImportDescription('');
    setImportDescriptionError('');
    setShowImportModal(true);
  };

  const closeImportModal = () => {
    if (importProcessing) return;
    setShowImportModal(false);
    setPendingGdriveBook(null);
    setImportDescription('');
    setImportDescriptionError('');
  };

  const handleProcessGdriveBook = async () => {
    if (!pendingGdriveBook) return;

    const description = importDescription.trim();
    if (description.length < 10) {
      setImportDescriptionError('Please write at least 10 characters describing this book.');
      return;
    }

    const { id: gdriveId, name: pdfFileName } = pendingGdriveBook;
    setImportProcessing(true);
    setImportDescriptionError('');

    try {
      const res = await fetch(`${API}/api/admin/gdrive/process/${gdriveId}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ description }),
      });
      const data = await readJsonBody(res);
      if (res.ok) {
        const label = pdfFileName && String(pdfFileName).trim() ? pdfFileName : 'PDF';
        alert(data?.message || `Import Successful: "${label}" has been uploaded and processed successfully.`);
        setShowImportModal(false);
        setPendingGdriveBook(null);
        setImportDescription('');
        setImportDescriptionError('');
        fetchData();
      } else {
        setImportDescriptionError(formatGdriveImportError(res, data, pdfFileName));
      }
    } catch (err) {
      console.error('Failed to process book:', err);
      setImportDescriptionError(`Import could not be completed: ${err?.message || 'Network or server error'}.`);
    } finally {
      setImportProcessing(false);
    }
  };

  const filteredBooks = books.filter(book => {
    const matchesSearch = book.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         book.id?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || book.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'ready': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'processing': return <Clock className="w-4 h-4 text-yellow-400 animate-spin" />;
      case 'queued': return <Clock className="w-4 h-4 text-blue-400" />;
      case 'error': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <AlertTriangle className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      ready: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      processing: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      queued: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      error: 'bg-red-500/20 text-red-400 border-red-500/30',
      pending: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-slate-800/50 border-r border-slate-700/50 flex flex-col z-40">
        {/* Logo */}
        <div className="p-6 border-b border-slate-700/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="font-bold text-lg">BookBlossom</h1>
              <p className="text-xs text-slate-400">Admin Dashboard</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {[
            { id: 'pipeline', icon: Activity, label: 'Pipeline Monitor' },
            { id: 'books', icon: BookOpen, label: 'Book Management' },
            { id: 'users', icon: Users, label: 'User Management' },
            { id: 'analytics', icon: BarChart3, label: 'Analytics' },
            { id: 'profile', icon: User, label: 'Profile' },
            { id: 'settings', icon: Settings, label: 'Settings' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                activeTab === item.id
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`nav-${item.id}`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        {/* User Info & Logout - Fixed at bottom */}
        <div className="p-4 border-t border-slate-700/50 bg-slate-900/50 mt-auto">
          <button
            onClick={() => setActiveTab('profile')}
            className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-700/50 transition-all mb-3"
          >
            <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-xl">
              {selectedAvatar || admin?.name?.[0] || 'A'}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="font-medium truncate">{admin?.name || 'Admin'}</p>
              <p className="text-xs text-slate-400 truncate">{admin?.email}</p>
            </div>
          </button>
          <button
            onClick={onLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 rounded-xl text-sm text-white font-semibold transition-all"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold">
              {activeTab === 'pipeline' && 'Pipeline Monitor'}
              {activeTab === 'books' && 'Book Management'}
              {activeTab === 'users' && 'User Management'}
              {activeTab === 'analytics' && 'Analytics'}
              {activeTab === 'profile' && 'Profile'}
              {activeTab === 'settings' && 'Settings'}
            </h2>
            <p className="text-slate-400 mt-1">
              {activeTab === 'pipeline' && 'Monitor book processing pipeline status'}
              {activeTab === 'books' && 'Manage uploaded and processed books'}
              {activeTab === 'users' && 'Create and manage student accounts'}
              {activeTab === 'analytics' && 'View processing metrics and insights'}
              {activeTab === 'profile' && 'Manage your profile and preferences'}
              {activeTab === 'settings' && 'Configure system settings'}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-all disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Pipeline Monitor Tab */}
        {activeTab === 'pipeline' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Total Books', value: stats?.total_books || 0, icon: BookOpen, color: 'emerald' },
                { label: 'Processing', value: stats?.processing || 0, icon: Cpu, color: 'yellow' },
                { label: 'Completed', value: stats?.completed || 0, icon: CheckCircle, color: 'blue' },
                { label: 'Failed', value: stats?.failed || 0, icon: XCircle, color: 'red' }
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-5"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-slate-400 text-sm">{stat.label}</span>
                    <div className={`w-8 h-8 bg-${stat.color}-500/20 rounded-lg flex items-center justify-center`}>
                      <stat.icon className={`w-4 h-4 text-${stat.color}-400`} />
                    </div>
                  </div>
                  <p className="text-3xl font-bold">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Google Drive Queue */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Cloud className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold">Google Drive</h3>
                    <p className="text-sm text-slate-400">
                      {gdriveConnected
                        ? 'PDFs are loaded from the KTo3 Books folder on Google Drive'
                        : 'Connect Google Drive to import PDFs from the KTo3 Books folder'}
                    </p>
                    {gdriveFolderUrl ? (
                      <a
                        href={gdriveFolderUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 mt-1"
                      >
                        Open import folder <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : null}
                  </div>
                </div>
                {gdriveConnected ? (
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" /> Connected
                    </span>
                    <button
                      onClick={handleDisconnectGoogleDrive}
                      className="px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm hover:bg-red-500/30 transition-all"
                    >
                      Disconnect
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleConnectGoogleDrive}
                    disabled={gdriveLoading}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 rounded-lg transition-all disabled:opacity-50"
                    data-testid="connect-gdrive"
                  >
                    {gdriveLoading ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span>Connecting...</span>
                      </>
                    ) : (
                      <>
                        <Cloud className="w-4 h-4" />
                        <span>Connect Google Drive</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {!gdriveConnected ? (
                <div className="text-center py-8 text-slate-500 bg-slate-900/30 rounded-xl">
                  <Cloud className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="mb-2">Google Drive not connected</p>
                  <p className="text-sm">Click "Connect Google Drive" to list PDFs from the import folder</p>
                </div>
              ) : gdriveBooks.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No PDF files found in the KTo3 Books folder</p>
                  <p className="text-sm mt-1">Make sure the connected Google account can open the KTo3 Books folder, then refresh</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  <p className="text-sm text-slate-400 mb-2">{gdriveBooks.length} PDF files found</p>
                  {gdriveBooks.map((book, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl hover:bg-slate-900/70 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-red-400" />
                        <div>
                          <p className="font-medium">{book.name}</p>
                          <p className="text-sm text-slate-400">
                            {book.size} • Modified {book.added_at}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => openImportModal(book)}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-all"
                        data-testid={`process-gdrive-${book.id}`}
                      >
                        <Download className="w-4 h-4" />
                        <span>Import & Process</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Processing Pipeline */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-400" />
                Processing Pipeline
              </h3>

              <div className="relative">
                {/* Pipeline stages */}
                <div className="flex items-center justify-between mb-8">
                  {['Upload', 'PDF Parse', 'Image Convert', 'AI Extract', 'Complete'].map((stage, idx) => (
                    <div key={idx} className="flex flex-col items-center relative z-10">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        idx < 4 ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400'
                      }`}>
                        {idx + 1}
                      </div>
                      <span className="text-xs text-slate-400 mt-2">{stage}</span>
                    </div>
                  ))}
                </div>
                {/* Progress line */}
                <div className="absolute top-6 left-6 right-6 h-0.5 bg-slate-700">
                  <div className="h-full bg-emerald-500 w-[80%] transition-all" />
                </div>
              </div>

              {/* All uploaded books */}
              <div className="mt-6 space-y-2">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm text-slate-400">
                    All uploaded books ({books.length})
                  </h4>
                  <button
                    type="button"
                    onClick={() => setActiveTab('books')}
                    className="text-xs text-emerald-400 hover:text-emerald-300"
                  >
                    Open Book Management →
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto space-y-2 pr-1">
                  {books.length === 0 ? (
                    <p className="text-sm text-slate-500 py-4 text-center">No uploaded books yet</p>
                  ) : (
                    books.map((book) => (
                      <div
                        key={book.id}
                        className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {getStatusIcon(book.status)}
                          <span className="font-medium truncate">
                            {book.title || book.original_filename}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <span className={`px-2 py-1 rounded text-xs border ${getStatusBadge(book.status)}`}>
                            {book.status}
                          </span>
                          <span className="text-sm text-slate-500">{book.pages || 0} pages</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Books Management Tab */}
        {activeTab === 'books' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <p className="text-slate-400 text-sm">
                Showing {filteredBooks.length} of {books.length} uploaded books
              </p>
              {booksLoadError ? (
                <p className="text-sm text-red-400" data-testid="books-load-error">
                  {booksLoadError}
                </p>
              ) : null}
            </div>
            {/* Search and Filter */}
            <div className="flex items-center gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search books by title or ID..."
                  className="w-full pl-12 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  data-testid="search-books"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                data-testid="status-filter"
              >
                <option value="all">All Status</option>
                <option value="ready">Ready</option>
                <option value="processing">Processing</option>
                <option value="queued">Queued</option>
                <option value="error">Error</option>
              </select>
            </div>

            {/* Books Table */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl overflow-hidden max-h-[calc(100vh-16rem)] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-slate-800 z-10">
                  <tr className="border-b border-slate-700/50">
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Book</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Status</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Pages</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Vocab</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Quiz</th>
                    <th className="text-left px-6 py-4 text-sm font-medium text-slate-400">Uploaded</th>
                    <th className="text-right px-6 py-4 text-sm font-medium text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBooks.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center py-12 text-slate-500">
                        <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No books found</p>
                      </td>
                    </tr>
                  ) : (
                    filteredBooks.map((book) => (
                      <tr key={book.id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center text-lg">
                              {book.emoji || '📚'}
                            </div>
                            <div>
                              <p className="font-medium">{book.title || 'Untitled'}</p>
                              <p className="text-xs text-slate-500">{book.id?.slice(0, 8)}...</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border ${getStatusBadge(book.status)}`}>
                            {getStatusIcon(book.status)}
                            {book.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-slate-300">{book.pages || 0}</td>
                        <td className="px-6 py-4 text-slate-300">
                          {book.vocabulary_count ?? book.vocabulary?.length ?? 0} words
                        </td>
                        <td className="px-6 py-4 text-slate-300">
                          {book.quiz_count ?? book.quiz?.length ?? 0} questions
                        </td>
                        <td className="px-6 py-4 text-slate-400 text-sm">
                          {book.uploaded_at ? new Date(book.uploaded_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleRegenerateVocabulary(book.id, book.title)}
                              className="p-2 hover:bg-blue-500/20 rounded-lg transition-all"
                              title="Regenerate Vocabulary"
                              data-testid={`regenerate-vocab-${book.id}`}
                            >
                              <BookText className="w-4 h-4 text-blue-400" />
                            </button>
                            <button
                              onClick={() => handleRegenerateQuiz(book.id, book.title)}
                              className="p-2 hover:bg-purple-500/20 rounded-lg transition-all"
                              title="Regenerate Quiz"
                              data-testid={`regenerate-quiz-${book.id}`}
                            >
                              <Brain className="w-4 h-4 text-purple-400" />
                            </button>
                            <button
                              onClick={() => handleEditBook(book)}
                              className="p-2 hover:bg-slate-700 rounded-lg transition-all"
                              title="Edit"
                              data-testid={`edit-book-${book.id}`}
                            >
                              <Edit3 className="w-4 h-4 text-slate-400" />
                            </button>
                            <button
                              onClick={() => handleApproveBook(book.id)}
                              className="p-2 hover:bg-emerald-500/20 rounded-lg transition-all"
                              title="Approve"
                              data-testid={`approve-book-${book.id}`}
                            >
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                            </button>
                            <button
                              onClick={() => handleDeleteBook(book.id)}
                              className="p-2 hover:bg-red-500/20 rounded-lg transition-all"
                              title="Delete"
                              data-testid={`delete-book-${book.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Create Users Card */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">Bulk Create Students</h3>
                    <p className="text-sm text-slate-400">Create multiple student accounts with auto-generated credentials</p>
                  </div>
                </div>
                <button
                  onClick={() => { setUserCreateResult(null); setShowUserModal(true); }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-xl font-medium transition-all"
                >
                  <UserPlus className="w-4 h-4" />
                  Create Students
                </button>
              </div>

              {/* Recent Creation Result */}
              {userCreateResult && (
                <div className={`p-4 rounded-xl ${userCreateResult.sheet_url ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                  <div className="flex items-center gap-3 mb-2">
                    <CheckCircle className={`w-5 h-5 ${userCreateResult.sheet_url ? 'text-emerald-400' : 'text-yellow-400'}`} />
                    <span className="font-medium">{userCreateResult.message}</span>
                  </div>
                  <div className="text-sm text-slate-400 space-y-1">
                    <p>Users created: {userCreateResult.start_number} to {userCreateResult.end_number}</p>
                    {userCreateResult.sheet_url && (
                      <>
                        <p className="text-xs text-slate-500">
                          📁 Saved to: <span className="text-emerald-400">{userCreateResult.folder_name || 'BookBlossom_Users'}</span> folder in Google Drive
                        </p>
                        <a 
                          href={userCreateResult.sheet_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open Google Sheet with credentials
                        </a>
                      </>
                    )}
                    {Array.isArray(userCreateResult.users) && userCreateResult.users.length > 0 && (
                      <div className="mt-3 max-h-48 overflow-auto rounded-lg border border-slate-700">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-900 text-slate-400">
                            <tr>
                              <th className="text-left px-3 py-2">Username</th>
                              <th className="text-left px-3 py-2">Password</th>
                            </tr>
                          </thead>
                          <tbody>
                            {userCreateResult.users.map((u) => (
                              <tr key={u.id || u.username} className="border-t border-slate-700">
                                <td className="px-3 py-2 font-mono text-slate-200">{u.username}</td>
                                <td className="px-3 py-2 font-mono text-slate-200">{u.password}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Existing Series */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold mb-4">Existing Series</h3>
              {userSeries.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No series created yet. Create your first batch of students above.</p>
              ) : (
                <div className="grid grid-cols-3 gap-4">
                  {userSeries.map((series, idx) => (
                    <div key={idx} className="bg-slate-900/50 p-4 rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-5 h-5 text-emerald-400" />
                        <span className="font-bold">{series.series_name}</span>
                      </div>
                      <p className="text-sm text-slate-400">
                        {series.last_number} students created
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Last: {series.series_name}{series.last_number}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Help Info */}
            <div className="bg-slate-800/30 border border-slate-700/30 rounded-xl p-4">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Bell className="w-4 h-4 text-blue-400" />
                How it works
              </h4>
              <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
                <li>Enter a series name (e.g., "Class5A", "SchoolXYZ")</li>
                <li>Specify how many students to create</li>
                <li>Usernames will be: SeriesName1, SeriesName2, etc.</li>
                <li>If you use the same series again, numbering continues (e.g., Class5A11, Class5A12)</li>
                <li>A Google Sheet with usernames & passwords will be saved to <strong>"BookBlossom_Users"</strong> folder in your Drive</li>
                <li>Students can login using their username at the reader app</li>
              </ul>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Overview Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-slate-400">Processing Success Rate</h3>
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <p className="text-4xl font-bold text-emerald-400">{stats?.success_rate || 95}%</p>
                <p className="text-sm text-slate-500 mt-2">+2.5% from last week</p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-slate-400">Avg Processing Time</h3>
                  <Clock className="w-5 h-5 text-blue-400" />
                </div>
                <p className="text-4xl font-bold">{stats?.avg_processing_time || '12.4'}s</p>
                <p className="text-sm text-slate-500 mt-2">Per book average</p>
              </div>

              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-slate-400">Storage Used</h3>
                  <HardDrive className="w-5 h-5 text-yellow-400" />
                </div>
                <p className="text-4xl font-bold">{stats?.storage_used_mb || 156} MB</p>
                <p className="text-sm text-slate-500 mt-2">of 10 GB quota</p>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-2 gap-6">
              {/* Processing by Day */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-emerald-400" />
                  Books Processed (Last 7 Days)
                </h3>
                <div className="flex items-end justify-between h-40 gap-2">
                  {[5, 8, 12, 7, 15, 10, 9].map((val, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-2">
                      <div
                        className="w-full bg-emerald-500/30 rounded-t hover:bg-emerald-500/50 transition-all"
                        style={{ height: `${(val / 15) * 100}%` }}
                      />
                      <span className="text-xs text-slate-500">
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][idx]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status Distribution */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-blue-400" />
                  Status Distribution
                </h3>
                <div className="flex items-center gap-8">
                  <div className="relative w-32 h-32">
                    <svg className="w-full h-full -rotate-90">
                      <circle cx="64" cy="64" r="56" fill="none" stroke="#334155" strokeWidth="16" />
                      <circle cx="64" cy="64" r="56" fill="none" stroke="#10b981" strokeWidth="16"
                        strokeDasharray={`${(stats?.completed || 85) * 3.5} 999`} />
                      <circle cx="64" cy="64" r="56" fill="none" stroke="#f59e0b" strokeWidth="16"
                        strokeDasharray={`${(stats?.processing || 10) * 3.5} 999`}
                        strokeDashoffset={`-${(stats?.completed || 85) * 3.5}`} />
                      <circle cx="64" cy="64" r="56" fill="none" stroke="#ef4444" strokeWidth="16"
                        strokeDasharray={`${(stats?.failed || 5) * 3.5} 999`}
                        strokeDashoffset={`-${((stats?.completed || 85) + (stats?.processing || 10)) * 3.5}`} />
                    </svg>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                      <span className="text-slate-400">Completed</span>
                      <span className="font-bold ml-auto">{stats?.completed || 85}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                      <span className="text-slate-400">Processing</span>
                      <span className="font-bold ml-auto">{stats?.processing || 10}%</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-red-500 rounded-full" />
                      <span className="text-slate-400">Failed</span>
                      <span className="font-bold ml-auto">{stats?.failed || 5}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-emerald-400" />
                Recent Activity
              </h3>
              <div className="space-y-3">
                {[
                  { action: 'Book processed', book: 'The Magic Forest', time: '2 minutes ago', status: 'success' },
                  { action: 'Book uploaded', book: 'Space Adventure.pdf', time: '15 minutes ago', status: 'info' },
                  { action: 'Processing failed', book: 'Corrupted.pdf', time: '1 hour ago', status: 'error' },
                  { action: 'Book approved', book: 'Ocean Friends', time: '2 hours ago', status: 'success' },
                  { action: 'Book deleted', book: 'Test Book', time: '3 hours ago', status: 'warning' }
                ].map((activity, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${
                        activity.status === 'success' ? 'bg-emerald-500' :
                        activity.status === 'error' ? 'bg-red-500' :
                        activity.status === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'
                      }`} />
                      <span>{activity.action}</span>
                      <span className="text-slate-400">- {activity.book}</span>
                    </div>
                    <span className="text-sm text-slate-500">{activity.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="max-w-2xl space-y-6">
            {/* Avatar Section */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-8">
              <h3 className="font-semibold mb-6 flex items-center gap-2">
                <User className="w-5 h-5 text-emerald-400" />
                Your Profile
              </h3>
              
              <div className="flex flex-col items-center mb-8">
                {/* Current Avatar */}
                <div className="relative mb-4">
                  <div className="w-32 h-32 bg-emerald-600 rounded-full flex items-center justify-center text-6xl shadow-xl">
                    {selectedAvatar || admin?.name?.[0] || 'A'}
                  </div>
                  <div className="absolute bottom-0 right-0 w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center border-4 border-slate-800">
                    <Camera className="w-5 h-5 text-slate-300" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold">{admin?.name || 'Admin'}</h2>
                <p className="text-slate-400">{admin?.email}</p>
                <span className="mt-2 px-4 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm capitalize">
                  {admin?.role || 'admin'}
                </span>
              </div>

              {/* Avatar Picker */}
              <div>
                <label className="block text-sm text-slate-400 mb-3">Choose your avatar</label>
                <div className="grid grid-cols-10 gap-2">
                  {avatarOptions.map((avatar, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedAvatar(avatar)}
                      className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-all ${
                        selectedAvatar === avatar
                          ? 'bg-emerald-500/30 ring-2 ring-emerald-500 scale-110'
                          : 'bg-slate-700/50 hover:bg-slate-700 hover:scale-105'
                      }`}
                      data-testid={`avatar-${idx}`}
                    >
                      {avatar}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Profile Info */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4">Account Information</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-400">Full Name</p>
                    <p className="font-medium">{admin?.name || 'Admin User'}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-400">Email Address</p>
                    <p className="font-medium">{admin?.email}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div>
                    <p className="text-sm text-slate-400">Role</p>
                    <p className="font-medium capitalize">{admin?.role || 'admin'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => {
                    setPasswordData({ current: '', new: '', confirm: '' });
                    setPasswordError('');
                    setPasswordSuccess(false);
                    setShowPasswordModal(true);
                  }}
                  className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-xl hover:bg-slate-700/50 transition-all"
                >
                  <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                    <Lock className="w-5 h-5 text-yellow-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium">Change Password</p>
                    <p className="text-xs text-slate-400">Update your password</p>
                  </div>
                </button>
                <button
                  onClick={onLogout}
                  className="flex items-center gap-3 p-4 bg-slate-900/50 rounded-xl hover:bg-red-500/20 transition-all"
                >
                  <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                    <LogOut className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-red-400">Logout</p>
                    <p className="text-xs text-slate-400">Sign out of dashboard</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-2xl space-y-6">
            {/* Account Settings */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-emerald-400" />
                Account Settings
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="font-medium">Email</p>
                      <p className="text-sm text-slate-400">{admin?.email}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-slate-700 text-slate-300 rounded-full text-sm capitalize">
                    {admin?.role || 'admin'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-yellow-400" />
                    <div>
                      <p className="font-medium">Password</p>
                      <p className="text-sm text-slate-400">Change your account password</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setPasswordData({ current: '', new: '', confirm: '' });
                      setPasswordError('');
                      setPasswordSuccess(false);
                      setShowPasswordModal(true);
                    }}
                    className="px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg transition-all"
                    data-testid="change-password-btn"
                  >
                    Change Password
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Bell className="w-5 h-5 text-emerald-400" />
                Email Notifications
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="font-medium">Processing Complete</p>
                      <p className="text-sm text-slate-400">Get notified when a book finishes processing</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-emerald-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <div>
                      <p className="font-medium">Processing Failed</p>
                      <p className="text-sm text-slate-400">Get notified when a book fails to process</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-emerald-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4">Google Drive Integration</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Cloud className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="font-medium">Google Drive Folder</p>
                      <p className="text-sm text-slate-400">BookBlossom/Uploads</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                    Connected (Mocked)
                  </span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Zap className="w-5 h-5 text-yellow-400" />
                    <div>
                      <p className="font-medium">Auto-Process</p>
                      <p className="text-sm text-slate-400">Automatically process new uploads</p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" defaultChecked />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:ring-2 peer-focus:ring-emerald-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4">AI Processing</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Cpu className="w-5 h-5 text-emerald-400" />
                    <div>
                      <p className="font-medium">OpenAI Integration</p>
                      <p className="text-sm text-slate-400">GPT-4o-mini for vocabulary extraction</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                    Active
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="font-semibold mb-4">Storage</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <HardDrive className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="font-medium">Local Storage</p>
                      <p className="text-sm text-slate-400">/app/backend/storage/</p>
                    </div>
                  </div>
                  <span className="text-slate-400">156 MB used</span>
                </div>
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-xl opacity-50">
                  <div className="flex items-center gap-3">
                    <Database className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-medium">Digital Ocean Spaces</p>
                      <p className="text-sm text-slate-400">Not configured</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-slate-700 text-slate-400 rounded-full text-sm">
                    Pending
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* GDrive Import Description Modal */}
      {showImportModal && pendingGdriveBook && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-lg p-6" data-testid="gdrive-import-modal">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Download className="w-5 h-5 text-emerald-400" />
                Import &amp; Process
              </h3>
              <button
                onClick={closeImportModal}
                disabled={importProcessing}
                className="p-2 hover:bg-slate-700 rounded-lg disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-400 mb-4">
              Describe <span className="text-white font-medium">{pendingGdriveBook.name}</span> before import.
              BookBlossom uses this description to choose the book&apos;s <strong className="text-slate-300">story path</strong> automatically.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2" htmlFor="import-description">
                  Book description <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="import-description"
                  value={importDescription}
                  onChange={(e) => {
                    setImportDescription(e.target.value);
                    if (importDescriptionError) setImportDescriptionError('');
                  }}
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white resize-y"
                  placeholder="e.g. A funny farm story about a brave little dog who makes new friends and learns kindness."
                  data-testid="import-description-input"
                  disabled={importProcessing}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Minimum 10 characters. Story path is assigned from this description only.
                </p>
              </div>

              {importDescriptionError ? (
                <p className="text-sm text-red-400" data-testid="import-description-error">
                  {importDescriptionError}
                </p>
              ) : null}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={closeImportModal}
                disabled={importProcessing}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl font-medium transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleProcessGdriveBook}
                disabled={importProcessing || importDescription.trim().length < 10}
                className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-description-submit"
              >
                {importProcessing ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Start Import
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Modal */}
      {showEditModal && selectedBook && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">Edit Book</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Title</label>
                <input
                  type="text"
                  value={editFormData.title}
                  onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white"
                  placeholder="Enter book title"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Emoji</label>
                <input
                  type="text"
                  value={editFormData.emoji}
                  onChange={(e) => setEditFormData({ ...editFormData, emoji: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white"
                  placeholder="📚"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Level</label>
                <select 
                  value={editFormData.level}
                  onChange={(e) => setEditFormData({ ...editFormData, level: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white"
                >
                  <option value="K">K</option>
                  <option value="K-1">K-1</option>
                  <option value="K-2">K-2</option>
                  <option value="K-3">K-3</option>
                  <option value="1-2">1-2</option>
                  <option value="2-3">2-3</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              >
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk User Create Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-blue-400" />
                Create Student Accounts
              </h3>
              <button
                onClick={() => setShowUserModal(false)}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!gdriveConnected && !userCreateResult && (
              <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                <p className="text-sm text-yellow-400">
                  Google Drive is not connected. You can still create students — usernames and passwords will be shown here so you can copy them.
                </p>
              </div>
            )}

            {userCreateResult?.users?.length > 0 && (
              <div className="mb-4 space-y-3">
                <div className={`p-3 rounded-xl ${userCreateResult.sheet_url ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                  <p className="text-sm font-medium">{userCreateResult.message}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Users created: {userCreateResult.start_number} to {userCreateResult.end_number}
                  </p>
                </div>
                <div className="max-h-56 overflow-auto rounded-xl border border-slate-700">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-900 text-slate-400">
                      <tr>
                        <th className="text-left px-3 py-2">Username</th>
                        <th className="text-left px-3 py-2">Password</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userCreateResult.users.map((u) => (
                        <tr key={u.id || u.username} className="border-t border-slate-700">
                          <td className="px-3 py-2 font-mono">{u.username}</td>
                          <td className="px-3 py-2 font-mono">{u.password}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const rows = [['username', 'password', 'email'], ...userCreateResult.users.map((u) => [u.username, u.password, u.email || ''])];
                    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `students_${userCreateResult.start_number}-${userCreateResult.end_number}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-2 text-sm text-blue-400 hover:text-blue-300"
                >
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Series Name</label>
                <input
                  type="text"
                  value={userFormData.series_name}
                  onChange={(e) => setUserFormData({ ...userFormData, series_name: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white"
                  placeholder="e.g., Class5A, SchoolXYZ"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Usernames will be: {userFormData.series_name || 'SeriesName'}1, {userFormData.series_name || 'SeriesName'}2, etc.
                </p>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Number of Students</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={userFormData.num_students}
                  onChange={(e) => setUserFormData({ ...userFormData, num_students: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white"
                />
                <p className="text-xs text-slate-500 mt-1">Maximum 100 students per batch</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowUserModal(false)}
                className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkCreateUsers}
                disabled={userCreating || !userFormData.series_name}
                className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-600 disabled:cursor-not-allowed rounded-xl font-medium transition-all flex items-center justify-center gap-2"
              >
                {userCreating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4" />
                    Create {userFormData.num_students} Students
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Lock className="w-5 h-5 text-emerald-400" />
                Change Password
              </h3>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="p-2 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {passwordSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-400" />
                </div>
                <h4 className="text-lg font-semibold text-emerald-400 mb-2">Password Changed!</h4>
                <p className="text-slate-400 text-sm">Your password has been updated successfully.</p>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="mt-6 px-6 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-medium transition-all"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={async (e) => {
                e.preventDefault();
                setPasswordError('');

                if (passwordData.new !== passwordData.confirm) {
                  setPasswordError('New passwords do not match');
                  return;
                }

                if (passwordData.new.length < 6) {
                  setPasswordError('Password must be at least 6 characters');
                  return;
                }

                try {
                  const res = await fetch(`${API}/api/admin/auth/change-password`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                      current_password: passwordData.current,
                      new_password: passwordData.new
                    })
                  });

                  const data = await res.json();

                  if (!res.ok) {
                    throw new Error(data.detail || 'Failed to change password');
                  }

                  setPasswordSuccess(true);
                } catch (err) {
                  setPasswordError(err.message);
                }
              }}>
                <div className="space-y-4">
                  {passwordError && (
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                      {passwordError}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Current Password</label>
                    <input
                      type="password"
                      value={passwordData.current}
                      onChange={(e) => setPasswordData({...passwordData, current: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      required
                      data-testid="current-password-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">New Password</label>
                    <input
                      type="password"
                      value={passwordData.new}
                      onChange={(e) => setPasswordData({...passwordData, new: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      required
                      data-testid="new-password-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-slate-400 mb-2">Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordData.confirm}
                      onChange={(e) => setPasswordData({...passwordData, confirm: e.target.value})}
                      className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 rounded-xl font-medium transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 rounded-xl font-medium transition-all flex items-center justify-center gap-2"
                    data-testid="submit-password-change"
                  >
                    <Key className="w-4 h-4" />
                    Update Password
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
